/**
 * Pure decision logic for reconciling the credit balance after a purchase whose
 * fulfilment is asynchronous (Stripe Checkout / webhook minting). The caller
 * polls the authoritative balance and asks this helper whether to stop.
 */

/** How long to keep polling for a webhook-minted balance before giving up. */
export const CREDIT_RECONCILE_TIMEOUT_MS = 30_000;
/** Gap between authoritative balance reads while reconciling. */
export const CREDIT_RECONCILE_INTERVAL_MS = 1_500;

export type CreditReconcileStep = "settled" | "retry" | "timed-out";

/**
 * @param baseline Balance observed before the purchase, or null/undefined when unknown.
 * @param observed Balance returned by the latest authoritative read, or null when it failed.
 * @param elapsedMs Time since reconciliation started.
 * @param timeoutMs Overall budget for reconciliation.
 */
export function nextCreditReconcileStep({
    baseline,
    observed,
    elapsedMs,
    timeoutMs = CREDIT_RECONCILE_TIMEOUT_MS,
}: {
    baseline: number | null | undefined;
    observed: number | null;
    elapsedMs: number;
    timeoutMs?: number;
}): CreditReconcileStep {
    const hasObservation = typeof observed === "number" && Number.isFinite(observed);

    // Without a baseline there is nothing to beat, so the first successful read
    // is already the freshest truth we can offer.
    if (hasObservation && (baseline === null || baseline === undefined)) {
        return "settled";
    }

    if (hasObservation && observed !== baseline) {
        return "settled";
    }

    return elapsedMs >= timeoutMs ? "timed-out" : "retry";
}
